import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ScanLine, Wallet, Gift, Palette, EyeOff, Megaphone, LineChart, MapPin } from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { BusinessMark, iconForBusiness } from "../components/BusinessMark";
import { WalletPass } from "../components/WalletPass";
import { PhoneMock } from "../components/PhoneMock";
import { LockScreen } from "../components/LockScreen";
import { BookingModal } from "../components/BookingModal";

// Single hero pass — locked to a real-feeling café (Ora Coffee) instead of
// cycling through industries. Keeps the hero focused and lets the customer's
// eye rest on one clear artefact.
const HERO_PROGRAM = {
  org_name: "Ora Coffee",
  pass_title: "Coffee Club",
  type: "stamp",
  brand_color: "#12100E",
  secondary_color: "#C8FF4D",
  reward: { name: "Free coffee", threshold: 10 },
};

// The BackPocket brand demo pass — a membership card featuring the BP mark.
// Matches the seeded org/program `/api/j/backpocket/try-us` on the backend.
// No strip image, no promised reward, membership type so it renders cleanly.

const BIZ_STEPS = [
  ["01", "Book a 15-minute call", "We learn how your business works and agree on the right rewards for your margins."],
  ["02", "We build it", "Your custom pass, rewards, staff scanner, dashboard and QR kit. Nothing for you to configure."],
  ["03", "You approve it", "You see the complete customer experience before anything goes live."],
  ["04", "Live in 48 hours", "The QR goes on the counter. Customers join in one scan and your team records visits in seconds."],
];

const PRODUCT_TABS = [
  { id: "design", tag: "Pass", title: "Custom to your brand", desc: "Your logo, colours and rewards on a pass that lives in Apple or Google Wallet." },
  { id: "operate", tag: "Scanner", title: "Any phone is the terminal", desc: "Staff scan the pass, enter their PIN and record the visit. No hardware, no POS change." },
  { id: "campaigns", tag: "Wallet", title: "Updates in real time", desc: "Every visit and reward appears on the customer's pass the moment it's recorded." },
  { id: "measure", tag: "Insights", title: "See who actually comes back", desc: "Members, repeat visits and rewards in one clear view. No sign-ups required." },
];

function ScannerScreen() {
  return (
    <div className="h-full w-full flex flex-col px-6 pt-8 pb-4" style={{ background: "radial-gradient(120% 80% at 50% 0%, #14181c 0%, #05080a 72%)" }}>
      <div className="flex flex-col items-center">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 220, damping: 15 }}
          className="h-[76px] w-[76px] rounded-full grid place-items-center shadow-[0_0_0_10px_rgba(200,255,77,0.12)]" style={{ background: "#C8FF4D" }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none"><path d="M20 6 9 17l-5-5" stroke="#0B0D10" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4 }} className="mt-6 text-white font-display font-bold text-[22px] tracking-[-0.01em]">Stamp added</motion.div>
        <div className="mt-1 text-white/50 text-[12.5px]">Ora Coffee · Coffee Club</div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28, duration: 0.4 }} className="mt-6 w-full max-w-[220px]">
          <div className="flex justify-between text-white/55 text-[11px] mb-2"><span className="tabular-nums">8 of 10</span><span>2 to go</span></div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: "80%" }} transition={{ delay: 0.35, duration: 0.9, ease: "easeOut" }} className="h-full rounded-full" style={{ background: "#C8FF4D" }} />
          </div>
        </motion.div>
        <div className="mt-6 flex gap-2">
          <div className="h-9 rounded-full px-4 flex items-center gap-1.5 bg-white/8 border border-white/10 text-white/85 text-[11px] font-medium">Add stamp</div>
          <div className="h-9 rounded-full px-4 flex items-center gap-1.5 bg-[#C8FF4D]/15 border border-[#C8FF4D]/30 text-[#C8FF4D] text-[11px] font-medium">Redeem</div>
        </div>
      </div>

      {/* Reward preview — one line, no duplicated progress */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.5 }}
        className="mt-auto rounded-[16px] border border-white/[0.08] bg-white/[0.03] px-3 py-3 flex items-center gap-3">
        <div className="h-10 w-10 rounded-[10px] grid place-items-center shrink-0" style={{ background: "rgba(200,255,77,0.15)" }}>
          <Gift size={17} className="text-[#C8FF4D]" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white text-[13px] font-semibold leading-tight truncate">Free coffee</div>
          <div className="text-white/45 text-[10.5px] mt-0.5">Two more and it's yours</div>
        </div>
        <div className="text-[#C8FF4D] text-[10.5px] uppercase tracking-[0.12em] font-semibold">Next</div>
      </motion.div>
    </div>
  );
}

function AnalyticsScreen() {
  const bars = [40, 56, 48, 67, 74, 61, 86, 97];
  return (
    <div className="h-full w-full flex flex-col px-5 pt-1.5 pb-4" style={{ background: "radial-gradient(120% 80% at 50% 0%, #1a1b1d 0%, #0a0a0b 62%)" }}>
      <div className="flex items-center justify-between">
        <div className="text-white/55 text-[12px] font-medium">Insights · 30 days</div>
        <div className="text-white/40 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-white/10">Live</div>
      </div>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }} className="mt-3">
        <div className="text-white font-display font-extrabold text-[38px] leading-none tracking-[-0.02em]">61<span className="text-white/45">%</span></div>
        <div className="mt-1.5 flex items-center gap-1.5 text-[11px]"><span className="text-[#C8FF4D] font-semibold">▲ 12</span><span className="text-white/45">repeat-visit rate</span></div>
      </motion.div>
      <div className="mt-6 flex items-end gap-[6px] h-[104px]">
        {bars.map((h, i) => (
          <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.15 + i * 0.055, type: "spring", stiffness: 130, damping: 18 }}
            className="flex-1 rounded-t-[3px]" style={{ background: i === bars.length - 1 ? "#C8FF4D" : "rgba(255,255,255,0.22)" }} />
        ))}
      </div>
      <div className="mt-5 grid grid-cols-2 gap-2">
        {[["Active members", "428"], ["Rewards redeemed", "312"]].map(([l, v], i) => (
          <motion.div key={l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 + i * 0.08 }} className="rounded-[14px] bg-white/[0.05] border border-white/[0.06] px-3 py-2.5">
            <div className="text-white font-display font-bold text-[18px] leading-none tracking-[-0.01em] tabular-nums">{v}</div>
            <div className="text-white/45 text-[9.5px] uppercase tracking-[0.12em] mt-1.5">{l}</div>
          </motion.div>
        ))}
      </div>

      {/* Latest activity — sharp, one word events */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.45 }}
        className="mt-3 rounded-[14px] bg-white/[0.04] border border-white/[0.06] px-3 py-2.5 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2 shrink-0">
          <span className="text-white/60 text-[10px] uppercase tracking-[0.14em]">Recent activity</span>
          <span className="text-white/35 text-[10px] uppercase tracking-[0.12em]">Live</span>
        </div>
        <div className="space-y-2">
          {[
            { name: "BP-00042", event: "Stamped", time: "2m", dot: "#C8FF4D" },
            { name: "BP-00317", event: "Reward redeemed", time: "18m", dot: "#6474FF" },
            { name: "BP-00108", event: "Joined", time: "1h", dot: "rgba(255,255,255,0.35)" },
            { name: "BP-00074", event: "Stamped", time: "2h", dot: "#C8FF4D" },
          ].map((r) => (
            <div key={r.name} className="flex items-center gap-2 text-[11px]">
              <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: r.dot }} />
              <span className="text-white/85 tabular-nums">{r.name}</span>
              <span className="flex-1 text-white/45">{r.event}</span>
              <span className="text-white/35 tabular-nums shrink-0">{r.time}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto pt-2 flex items-center justify-between text-[9.5px] uppercase tracking-[0.14em] text-white/35 border-t border-white/[0.06] mt-2.5">
          <span>Refreshed 2m ago</span>
          <span className="flex items-center gap-1 text-[#C8FF4D]/80">See all<ArrowRight size={9} strokeWidth={2.4} /></span>
        </div>
      </motion.div>
    </div>
  );
}

function NearbyScreen() {
  const program = { org_name: "Ora Coffee", pass_title: "Coffee Club", type: "stamp", brand_color: "#12100E", secondary_color: "#C8FF4D", reward: { name: "Free coffee", threshold: 10 } };
  return (
    <div className="h-full w-full relative flex flex-col" style={{ background: "linear-gradient(180deg,#1e1a14 0%,#0d0b08 55%,#000 100%)" }}>
      {/* time + date */}
      <div className="text-center pt-3 shrink-0">
        <div className="text-white/75 text-[13px] font-medium">Tuesday, 9 June</div>
        <div className="text-white font-display font-bold tracking-tight" style={{ fontSize: 62, lineHeight: 1.02 }}>9:41</div>
      </div>

      {/* live nearby chip */}
      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.35 }}
        className="mx-auto mt-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.12] border border-white/10 backdrop-blur-md">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inset-0 rounded-full bg-[#C8FF4D] animate-ping opacity-60" />
          <span className="relative rounded-full h-1.5 w-1.5 bg-[#C8FF4D]" />
        </span>
        <MapPin size={10} strokeWidth={2.2} className="text-white/85" />
        <span className="text-white/90 text-[10.5px] uppercase tracking-[0.14em] font-medium">At Ora Coffee · 40 m</span>
      </motion.div>

      {/* auto-surfaced wallet pass — glassy, floating, tap-ready */}
      <motion.div initial={{ y: 22, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} transition={{ delay: 0.32, type: "spring", stiffness: 220, damping: 22 }}
        className="mx-3 mt-auto mb-4"
        data-testid="nearby-pass">
        <div className="rounded-[22px] overflow-hidden shadow-[0_20px_50px_-16px_rgba(0,0,0,0.6)] border border-white/10 relative">
          <div className="px-4 pt-3.5 pb-4" style={{ background: program.brand_color }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-[7px] grid place-items-center" style={{ background: program.secondary_color, color: "#0B0D10" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M4 11h13a4 4 0 0 1 0 8H4M4 7h11v4H4zM6 5v2M10 5v2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                </div>
                <div>
                  <div className="text-white/60 text-[9.5px] uppercase tracking-[0.14em]">Coffee Club</div>
                  <div className="text-white text-[13.5px] font-semibold leading-tight">Ora Coffee</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-white/50 text-[9.5px] uppercase tracking-[0.12em]">Reward</div>
                <div className="text-white text-[12px] font-semibold tabular-nums">8 / 10</div>
              </div>
            </div>
            {/* mini stamp grid */}
            <div className="mt-3 grid grid-cols-10 gap-1">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-4 rounded-full grid place-items-center" style={{ background: i < 8 ? program.secondary_color : "rgba(255,255,255,0.09)" }}>
                  {i < 8 && <svg width="8" height="8" viewBox="0 0 24 24" fill="none"><path d="M20 6 9 17l-5-5" stroke="#0B0D10" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
              ))}
            </div>
          </div>
          <div className="px-4 py-2 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.1)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}>
            <span className="text-white/80 text-[11px] font-medium">Show to staff to check in</span>
            <span className="text-[#C8FF4D] text-[10.5px] uppercase tracking-[0.14em] font-semibold">Tap</span>
          </div>
        </div>
      </motion.div>

      <div className="pb-6 px-8 flex items-center justify-between shrink-0">
        <div className="h-11 w-11 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.35)" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff" stroke="#fff" strokeWidth="1"><path d="M6 4h4v3l-2 3h-2z" /><path d="M6 14h4v6H6z" /></svg>
        </div>
        <div className="h-11 w-11 rounded-full grid place-items-center" style={{ background: "rgba(0,0,0,0.35)" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8"><rect x="3" y="7" width="18" height="12" rx="2" /><circle cx="12" cy="13" r="3" /></svg>
        </div>
      </div>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 h-1 w-28 rounded-full bg-white/75" />
    </div>
  );
}

function WalletScreen({ program, wallet = "apple", state = {}, others = [] }) {  return (
    <div className="h-full w-full flex flex-col overflow-hidden" style={{ background: "radial-gradient(120% 80% at 50% 0%, #232427 0%, #0a0a0b 62%, #000 100%)" }}>
      <div className="px-5 pt-1 pb-1 flex items-center justify-between shrink-0">
        <div className="text-white font-display font-bold text-[15px] tracking-[-0.01em]">Wallet</div>
        <div className="text-white/40 text-[10px] uppercase tracking-[0.14em]">{others.length + 1} passes</div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="mt-2 flex justify-center shrink-0">
        <div style={{ transform: "scale(0.9)", transformOrigin: "top center" }}>
          <WalletPass program={program} wallet={wallet} state={state} />
        </div>
      </motion.div>

      {/* Stacked pass peeks sit tight below the primary pass — no big black gap */}
      <div className="mt-2 shrink-0 relative">
        {others.map((o, i) => {
          const OIcon = iconForBusiness(o.name);
          return (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08, duration: 0.45, ease: [0.2, 0.6, 0.2, 1] }}
            className="mx-3 rounded-[16px] px-3.5 py-2.5 flex items-center gap-2.5 shadow-[0_-6px_18px_-10px_rgba(0,0,0,0.55)] relative"
            style={{ background: o.color, marginTop: i === 0 ? 0 : -8, zIndex: others.length - i }}>
            <div className="h-7 w-7 rounded-[7px] grid place-items-center shrink-0" style={{ background: o.accent, color: "#0B0D10" }}><OIcon size={13} strokeWidth={2.2} /></div>
            <div className="min-w-0 flex-1">
              <div className="text-white text-[11.5px] font-semibold truncate leading-tight">{o.name}</div>
              <div className="text-white/55 text-[9.5px] uppercase tracking-[0.11em] mt-0.5">{o.title}</div>
            </div>
            <div className="text-white/70 text-[10.5px] font-medium tabular-nums shrink-0">{o.status}</div>
          </motion.div>
          );
        })}
      </div>

      {/* Bottom hint filling residual space, tied to brand story */}
      <div className="mt-auto pb-2 text-center">
        <div className="text-white/25 text-[9.5px] uppercase tracking-[0.16em]">Passes update automatically</div>
      </div>
    </div>
  );
}

const CAFE_STACK = [
  { name: "Sharp & Sons", title: "The Chair Club", color: "#14202E", accent: "#6ED0FF", initials: "SS", status: "3/6" },
  { name: "Bloom Studio", title: "Beauty Points", color: "#2A1522", accent: "#FF9ECF", initials: "BS", status: "240 pts" },
];

function ProductShowcase() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setIdx((p) => (p + 1) % PRODUCT_TABS.length), 4800);
    return () => clearTimeout(t);
  }, [idx]);
  const active = PRODUCT_TABS[idx];

  const renderScreen = () => {
    switch (active.id) {
      case "operate": return <PhoneMock width={296} screen={<ScannerScreen />} />;
      case "campaigns": return <PhoneMock width={296} screen={<LockScreen />} />;
      case "measure": return <PhoneMock width={296} screen={<AnalyticsScreen />} />;
      default: return <PhoneMock width={296} screen={<WalletScreen program={HERO_PROGRAM} wallet="apple" state={{ balance: 8, pass_number: "BP-00042" }} others={CAFE_STACK} />} />;
    }
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-12 lg:gap-16 items-start">
      <div className="lg:pt-2">
        <p className="text-[13px] uppercase tracking-[0.2em] text-[#3a3d44]/50 mb-5">Everything you need</p>
        <h2 className="font-display font-extrabold tracking-[-0.035em] text-[clamp(2.1rem,4.6vw,3.4rem)] leading-[1.0]">Custom loyalty.<br /><span className="text-[#3a3d44]/40">Built around your business.</span></h2>
        <p className="mt-6 text-[16.5px] leading-relaxed text-[#3a3d44] max-w-xl">Visits, points or membership. Designed for your brand, run by your team, kept in your customers' Wallet.</p>
        <div className="mt-12" data-testid="product-tabs">
        {PRODUCT_TABS.map((tab, i) => {
          const on = i === idx;
          return (
            <button key={tab.id} data-testid={`product-tab-${tab.id}`} onClick={() => setIdx(i)}
              className="group w-full text-left border-t border-[#E4E0D6] last:border-b py-5 relative">
              <span className="absolute left-0 top-4 bottom-4 w-[3px] rounded-full bg-[#C8FF4D] origin-top transition-transform duration-300" style={{ transform: `scaleY(${on ? 1 : 0})` }} />
              <div className="flex items-center gap-4 pl-6">
                <span className={`font-display font-extrabold text-[15px] tabular-nums transition-colors ${on ? "text-[#0B0D10]" : "text-[#3a3d44]/30"}`}>0{i + 1}</span>
                <h3 className={`font-display font-bold text-[19px] lg:text-[22px] tracking-[-0.01em] transition-colors ${on ? "text-[#0B0D10]" : "text-[#3a3d44]/45 group-hover:text-[#3a3d44]/85"}`}>{tab.title}</h3>
                <span className="ml-auto text-[11px] uppercase tracking-[0.16em] text-[#3a3d44]/35 shrink-0">{tab.tag}</span>
              </div>
              <AnimatePresence initial={false}>
                {on && (
                  <motion.div key="d" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: [0.2, 0.6, 0.2, 1] }} className="overflow-hidden pl-[46px]">
                    <p className="text-[15px] text-[#3a3d44]/80 leading-relaxed max-w-lg pt-2.5">{tab.desc}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          );
        })}
        </div>
      </div>

      <div className="flex justify-center lg:justify-end lg:sticky lg:top-24">
        <div className="relative">
          <div className="absolute -inset-10 rounded-full bg-gradient-to-tr from-[#6474FF]/12 to-[#C8FF4D]/22 blur-3xl" />
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div key={active.id} initial={{ opacity: 0, y: 22, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -18, scale: 0.97 }} transition={{ type: "spring", stiffness: 190, damping: 22 }}>
                {renderScreen()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}


function AnalyticsChart() {
  // 8-week repeat-visits sample — rendered as a proper area chart with
  // gradient fill, animated line, peak marker and a delta callout.
  const data = [38, 52, 47, 63, 71, 66, 82, 94];
  const w = 360, h = 200;
  const padX = 14, padY = 22;
  const min = Math.min(...data), max = Math.max(...data);
  const stepX = (w - padX * 2) / (data.length - 1);
  const points = data.map((v, i) => {
    const x = padX + i * stepX;
    const y = padY + (1 - (v - min) / (max - min || 1)) * (h - padY * 2);
    return [x, y];
  });
  // smooth curved path via cubic bezier midpoints
  const line = points.reduce((acc, p, i, arr) => {
    if (i === 0) return `M ${p[0]} ${p[1]}`;
    const prev = arr[i - 1];
    const cx = (prev[0] + p[0]) / 2;
    return acc + ` C ${cx} ${prev[1]}, ${cx} ${p[1]}, ${p[0]} ${p[1]}`;
  }, "");
  const area = line + ` L ${points[points.length - 1][0]} ${h - padY} L ${points[0][0]} ${h - padY} Z`;
  const delta = Math.round(((data[data.length - 1] - data[0]) / data[0]) * 100);
  const peak = points[points.length - 1];

  return (
    <div className="rounded-[24px] bg-white/[0.04] border border-white/10 p-7 backdrop-blur-sm">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 font-medium">Repeat visits</div>
          <div className="flex items-baseline gap-2.5 mt-2">
            <span className="font-display font-extrabold text-[38px] leading-none tracking-[-0.02em] tabular-nums">{data[data.length - 1]}</span>
            <span className="text-white/55 text-[13.5px]">visits this week</span>
          </div>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-[#C8FF4D]/15 border border-[#C8FF4D]/25 px-2 py-0.5 text-[#C8FF4D] font-semibold text-[11.5px] tabular-nums">▲ 18%</span>
            <span className="text-[11.5px] text-white/45">vs. last month</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/40">Peak</div>
          <div className="font-display font-bold text-[18px] tabular-nums mt-0.5">{max}</div>
        </div>
      </div>

      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
        <defs>
          <linearGradient id="bp-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C8FF4D" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#C8FF4D" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* horizontal reference grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={padX} x2={w - padX} y1={padY + f * (h - padY * 2)} y2={padY + f * (h - padY * 2)} stroke="rgba(255,255,255,0.06)" strokeDasharray="3 4" />
        ))}
        <motion.path initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.35 }} d={area} fill="url(#bp-area)" />
        <motion.path initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.4, ease: [0.2, 0.6, 0.2, 1] }} d={line} fill="none" stroke="#C8FF4D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* peak marker */}
        <motion.g initial={{ scale: 0, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.2, type: "spring", stiffness: 220, damping: 18 }} style={{ transformOrigin: `${peak[0]}px ${peak[1]}px` }}>
          <circle cx={peak[0]} cy={peak[1]} r="8" fill="#0B0D10" stroke="#C8FF4D" strokeWidth="2.5" />
          <circle cx={peak[0]} cy={peak[1]} r="3" fill="#C8FF4D" />
        </motion.g>
      </svg>

      <div className="mt-3 flex items-center justify-between text-[10.5px] text-white/40">
        <span>Wk 1</span><span>Wk 2</span><span>Wk 3</span><span>Wk 4</span><span>Wk 5</span><span>Wk 6</span><span>Wk 7</span><span className="text-white/70 font-medium">Wk 8 · now</span>
      </div>
    </div>
  );
}

function Nav({ onBook }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F4F1E9]/75 border-b border-[#E4E0D6]">
      <div className="max-w-[1180px] mx-auto px-6 h-[68px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
          <BrandMark size={30} />
          <span className="font-display font-extrabold text-[19px] tracking-tight">BackPocket</span>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-[14px] text-[#3a3d44]">
          <a href="#how" className="link-underline">How it works</a>
          <a href="#features" className="link-underline">Features</a>
          <a href="#analytics" className="link-underline">Results</a>
        </nav>
        <div className="flex items-center gap-2">
          <button data-testid="nav-book-btn" onClick={onBook} className="btn-lift bg-[#0B0D10] text-white text-[14px] font-medium rounded-full px-5 py-2.5 hover:bg-[#25282E]">Book a call</button>
        </div>
      </div>
    </header>
  );
}

function Marquee() {
  const items = ["Cafés", "Bakeries", "Barbers", "Hair Salons", "Nail Salons", "Beauty Studios", "Pilates", "Yoga", "Gyms", "Massage", "Restaurants", "Pizzerias", "Wine Bars", "Breweries", "Florists", "Pet Groomers", "Juice Bars", "Ice Cream Shops"];
  const row = [...items, ...items];
  return (
    <div className="border-y border-[#E4E0D6] bg-[#EFEBE1] overflow-hidden py-4">
      <div className="marquee-track flex gap-10 whitespace-nowrap w-max">
        {row.map((t, i) => (
          <span key={i} className="flex items-center gap-10 text-[15px] text-[#3a3d44]/70">
            {t}<span className="h-1.5 w-1.5 rounded-full bg-[#C8FF4D]" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* --------------------- PROBLEM STORY (editorial statement + horizontal beat rail) --------------------- */

const PROBLEM_BEATS = [
  ["They scan", "Your QR on the counter. One scan opens the pass."],
  ["They save", "One tap adds it to Apple or Google Wallet."],
  ["You record", "Staff scan the pass and record the visit in seconds."],
  ["They earn", "The pass updates instantly. Rewards unlock automatically."],
];

function ProblemStory() {
  return (
    <section className="max-w-[1180px] mx-auto px-6 py-24 lg:py-32">
      {/* Editorial statement — narrow, magazine-style */}
      <div className="max-w-3xl">
        <p className="text-[13px] uppercase tracking-[0.2em] text-[#0B0D10]/45 mb-5">The problem</p>
        <h2 className="font-display font-extrabold tracking-[-0.03em] leading-[1.02] text-[clamp(2.2rem,5vw,3.8rem)]">
          A loyalty card they won't <span className="font-serif-editorial italic font-normal tracking-normal">leave at home.</span>
        </h2>
        <p className="mt-7 text-[17px] leading-relaxed text-[#3a3d44] max-w-[38rem]">
          Paper cards get lost. Apps don't get downloaded. BackPocket lives where customers already keep their cards: <span className="text-[#0B0D10] font-medium">their Wallet.</span>
        </p>
      </div>

      {/* Horizontal 4-beat rail — the customer journey in one clean line.
          Hairline dotted rule connects the beats on desktop. No boxes, no icons,
          no big serif numbers — the typography carries the whole thing. */}
      <div className="mt-20 lg:mt-24 relative">
        {/* connective dotted rail (desktop only) */}
        <div className="hidden md:block absolute top-[26px] left-6 right-6 h-px pointer-events-none"
          style={{ backgroundImage: "linear-gradient(to right, rgba(11,13,16,0.18) 40%, transparent 40%)", backgroundSize: "8px 1px", backgroundRepeat: "repeat-x" }} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 md:gap-x-8 relative">
          {PROBLEM_BEATS.map(([title, body], i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.09, duration: 0.55, ease: [0.2, 0.6, 0.2, 1] }}
              className="relative pl-0" data-testid={`problem-beat-${i + 1}`}>
              {/* editorial numeric anchor — small, tabular, ink */}
              <div className="flex items-center gap-3 mb-4 relative">
                <span className="relative z-10 h-[14px] w-[14px] rounded-full bg-[#0B0D10] grid place-items-center shrink-0">
                  <span className="h-[5px] w-[5px] rounded-full bg-[#C8FF4D]" />
                </span>
                <span className="font-display font-bold text-[#0B0D10] text-[13px] tabular-nums tracking-[0.02em]">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <h3 className="font-display font-bold text-[19px] lg:text-[21px] tracking-[-0.01em] leading-tight text-[#0B0D10]">{title}</h3>
              <p className="mt-2.5 text-[14.5px] leading-relaxed text-[#3a3d44]/75 max-w-[16rem]">{body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Marketing() {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);


  return (
    <div className="min-h-screen bg-[#F4F1E9] text-[#0B0D10] overflow-x-hidden">
      <Nav onBook={openBooking} />

      {/* HERO */}
      <section className="max-w-[1180px] mx-auto px-6 pt-16 pb-10 lg:pt-24 lg:pb-16 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 items-center">
        <div>
          <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[0.95] text-[clamp(2.6rem,6.2vw,4.6rem)]">
            Your custom loyalty system. <br className="hidden sm:block" /><span className="font-serif-editorial italic font-normal tracking-normal">Live in 48 hours.</span>
          </h1>
          <p className="mt-7 text-[17px] leading-relaxed text-[#3a3d44] max-w-[32rem]">
            We build, brand and launch your loyalty program inside Apple and Google Wallet. No customer app. No new POS. Nothing for you to configure.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button data-testid="hero-book-btn" onClick={openBooking} className="btn-lift group bg-[#C8FF4D] text-[#0B0D10] rounded-full h-[52px] pl-6 pr-5 text-[16px] font-semibold flex items-center gap-2 shadow-[0_10px_30px_-8px_rgba(200,255,77,0.7)]">
              Book a 15-min call
              <span className="grid place-items-center h-8 w-8 rounded-full bg-[#0B0D10] text-[#C8FF4D] group-hover:rotate-45 transition-transform"><ArrowRight size={16} /></span>
            </button>
            <a href="#how" className="btn-lift h-[52px] px-6 rounded-full border border-[#0B0D10]/15 text-[16px] font-medium flex items-center hover:border-[#0B0D10] transition-colors">See how it works</a>
          </div>
        </div>

        {/* pass on a phone — locked to Ora Coffee, no cycling */}
        <div className="relative flex flex-col items-center">
          <div className="relative">
            <div className="absolute -inset-10 rounded-full bg-gradient-to-br from-[#C8FF4D]/25 via-transparent to-[#6474FF]/15 blur-3xl" />
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="relative">
              <PhoneMock width={290} label="Wallet">
                <WalletPass program={HERO_PROGRAM} wallet="apple" state={{ balance: 6, points: 240, pass_number: "BP-00042" }} />
              </PhoneMock>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* PROBLEM — editorial cinematic 4-scene spread */}
      <ProblemStory />

      {/* HOW IT WORKS — business side (big-number layout) */}
      <section id="how" className="bg-[#0B0D10] text-white grain overflow-hidden">
        <div className="max-w-[1180px] mx-auto px-6 py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-[1fr_320px] gap-10 lg:gap-16 items-end">
            <div>
              <p className="text-[13px] uppercase tracking-[0.2em] text-[#C8FF4D] mb-5">How it works</p>
              <h2 className="font-display font-extrabold tracking-[-0.03em] text-[clamp(2.1rem,4.8vw,3.6rem)] leading-[0.98]">
                We build it.<br /><span className="text-white/35">You launch it.</span>
              </h2>
            </div>
            <div>
              <p className="text-white/55 text-[15.5px] leading-relaxed">No new terminal. No POS change. We set everything up around your business and show your team how it works.</p>
              <button onClick={openBooking} data-testid="business-cta" className="btn-lift mt-6 inline-flex items-center gap-2 rounded-full border border-[#C8FF4D]/40 text-[#C8FF4D] h-12 px-6 font-semibold hover:bg-[#C8FF4D] hover:text-[#0B0D10] transition-colors">
                Book a 15-min call <ArrowRight size={17} />
              </button>
            </div>
          </div>

          <div className="mt-14 lg:mt-24 grid sm:grid-cols-2 gap-x-12 lg:gap-x-20">
            {BIZ_STEPS.map(([n, t, d], i) => (
              <motion.div key={n} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 2) * 0.1, type: "spring", stiffness: 120, damping: 18 }}
                className={`group relative flex items-start gap-5 lg:gap-8 py-9 border-t border-white/10 ${i >= 2 ? "sm:border-t-0 sm:pt-0" : ""} ${i % 2 === 1 ? "sm:mt-16" : ""}`}>
                <span className="font-display font-extrabold shrink-0 leading-[0.72] text-transparent group-hover:text-[#C8FF4D] transition-colors duration-300 [-webkit-text-stroke:1.5px_rgba(200,255,77,0.5)]" style={{ fontSize: "clamp(3.4rem,6.5vw,5.4rem)" }}>{n}</span>
                <div className="pt-2 lg:pt-3.5">
                  <h3 className="font-display font-bold text-[20px] lg:text-[23px] tracking-[-0.01em]">{t}</h3>
                  <p className="text-white/55 text-[15px] leading-relaxed mt-2.5 max-w-[26rem]">{d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE — interactive tabbed device preview */}
      <section id="features" className="max-w-[1180px] mx-auto px-6 py-20 lg:py-28">
        <ProductShowcase />
      </section>

      {/* ANALYTICS band — big-number */}
      <section id="analytics" className="bg-[#0B0D10] text-white grain overflow-hidden">
        <div className="max-w-[1180px] mx-auto px-6 py-24 lg:py-32 relative z-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <p className="text-[13px] uppercase tracking-[0.2em] text-[#C8FF4D] mb-5">Insights</p>
            <h2 className="font-display font-extrabold tracking-[-0.03em] text-[clamp(2rem,4.4vw,3.3rem)] leading-[1.0]">See whether they're <br /><span className="text-white/35">coming back.</span></h2>
            <p className="mt-5 text-[15.5px] text-white/55 max-w-md leading-relaxed">Members, repeat visits and rewards redeemed. A clear view of whether your program is working.</p>
            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-8 border-t border-white/10 pt-8">
              {[["61%", "returned again", true], ["4.8", "avg visits / member", false], ["312", "rewards redeemed", false]].map(([v, l, hot]) => (
                <div key={l}>
                  <div className="font-display font-extrabold tracking-[-0.02em] leading-[0.85]" style={{ fontSize: "clamp(2.2rem,4.6vw,3.4rem)", color: hot ? "#C8FF4D" : "#fff" }}>{v}</div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-white/45 mt-2.5">{l}</div>
                </div>
              ))}
            </div>
          </div>
          <AnalyticsChart />
        </div>
      </section>

      {/* TRY IT — public BackPocket-branded pass anyone can install */}

      {/* CLOSING — lime lead-gen band (booking is the only door) */}
      <section className="bg-[#C8FF4D]">
        <div className="max-w-[1180px] mx-auto px-6 py-24 text-center">
          <h2 className="font-display font-extrabold tracking-[-0.03em] text-[clamp(2.2rem,5.5vw,4rem)] text-[#0B0D10] leading-[0.98]">Your loyalty program could be live in <span className="font-serif-editorial italic font-normal tracking-normal">48 hours.</span></h2>
          <p className="mt-5 text-[16px] text-[#0B0D10]/75 max-w-xl mx-auto leading-relaxed">Book a short call. We'll choose the right rewards, show you the customer experience and build everything for you.</p>
          <button data-testid="footer-cta" onClick={openBooking} className="btn-lift mt-9 bg-[#0B0D10] text-white rounded-full h-[56px] px-9 font-semibold text-[16px] inline-flex items-center gap-2">
            Book a 15-min call <ArrowUpRight size={18} />
          </button>
          <div className="mt-5 text-[12.5px] text-[#0B0D10]/55">No obligation. 15 minutes.</div>
        </div>
      </section>

      <footer className="bg-[#0B0D10] text-white/70">
        <div className="max-w-[1180px] mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <BrandMark size={26} tone="light" />
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white text-[17px]">BackPocket</div>
              <div className="text-[12px] text-white/45 mt-0.5">Loyalty that lives in their Wallet.</div>
            </div>
          </div>
          <div className="flex items-center gap-8 text-[14px]">
            <Link to="/privacy" className="link-underline hover:text-white">Privacy</Link>
            <Link to="/terms" className="link-underline hover:text-white">Terms</Link>
            <button onClick={openBooking} className="link-underline hover:text-white" data-testid="footer-book-link">Book a call</button>
            <Link to="/login" className="link-underline text-white/30 hover:text-white/70 text-[13px]" data-testid="footer-admin-link" title="Admin sign in">Admin</Link>
          </div>
          <span className="text-[13px] text-white/40">© 2026 BackPocket</span>
        </div>
      </footer>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
